import Button from 'Components/Base/Button'
import ResponsiveContainer from 'Components/Base/ResponsiveContainer'
import dayjs from 'dayjs'
import { useAppDispatch, useAppSelector } from 'hooks/hooks'
import useAnalytics from 'hooks/useAnalytics'
import useDashboard from 'hooks/useDashboard'
import useDocumentTitle from 'hooks/useDocumentTitle'
import React, { useEffect, useState } from 'react'
import { Dropdown } from 'react-bootstrap'
import Row from 'react-bootstrap/Row'
import ReactDatePicker from 'react-datepicker'
import { useNavigate } from 'react-router-dom'
import { getAccessToken } from 'reducers/authSlice'
import { getScaleReadings, getScaleReadingsSummary, loadScaleReadingSummary } from 'reducers/dashboardSlice'
import { getProfile } from 'reducers/profileSlice'
import { setLoading } from 'reducers/tempSlice'
import { useTheme } from 'styled-components'
import { ChartReading } from 'types/ChartReading'
import { DateSetting, ScaleReading } from 'types/Dashboards'
import ContentCards from '../DashboardGlucosePage/components/ContentCards'
import { filterOptions } from '../constants'
import './DashboardScalePage.scss'
import AddWeightModal from './components/DigitalScale/addWeight'
import GridCards from './components/GridCards/GridCards'
import LineChart from './components/LineChart'
import ReadingDataGrid from './components/ReadingDataGrid'

export default function DashboardDigitalScalePage() {
  const { setDocumentTitle } = useDocumentTitle()
  setDocumentTitle('Digital Scale')
  const theme = useTheme()
  const dispatch = useAppDispatch()
  const accessToken = useAppSelector(getAccessToken)
  const analytics = useAnalytics(accessToken!)
  const [isMobile, setIsMobile] = useState<boolean>(false)
  const [readings, setReadings] = useState<ScaleReading[]>([])
  const [chartHeight, setChartHeight] = useState(300)
  const [showCustomFields, setShowCustomFields] = useState<boolean>()
  const [chartReadings, setChartReadings] = useState<ChartReading[]>()
  const [fatReadings, setFatReadings] = useState<ChartReading[]>()
  const [muscleReadings, setMuscleReadings] = useState<ChartReading[]>()
  const [waterReadings, setWaterReadings] = useState<ChartReading[]>()
  const [readingFilter, setReadingFilter] = useState<DateSetting>({
    label: 'Last 30 Days',
    value: 30,
  })
  const [dateRange, setDateRange] = useState<{ startDate: Date | undefined; endDate: Date | undefined }>({
    startDate: undefined,
    endDate: undefined,
  })

  const scaleReadings = useAppSelector(getScaleReadings)
  const scaleReadingsSummary = useAppSelector(getScaleReadingsSummary)
  const { updateWeight } = useDashboard()
  const [addWeightModal, setAddWeightModal] = useState<boolean>(false)
  const profile = useAppSelector(getProfile)
  const [welltrak, setWelltrak] = useState(0)
  const navigate = useNavigate()

  useEffect(() => {
    dispatch(setLoading(true))
    dispatch(
      loadScaleReadingSummary({
        token: accessToken,
      }),
    ).then(() => dispatch(setLoading(false)))

    setMobileSetting()
    window.addEventListener('resize', () => {
      setMobileSetting()
    })
  }, [])

  useEffect(() => {
    changeReadingFilter({
      label: 'Last 30 Days',
      value: 30,
    })
  }, [scaleReadings])

  useEffect(() => {
    if (readingFilter.label === 'All Readings') {
      analytics.postAnalyticsEvent({
        category: `reading_filter_dashboardDigitalScale_allReadings`,
        action: `reading_filter_dashboardDigitalScale_allReadings`,
        label: `Button`,
        screenName: 'DigitalScaleDashboard',
      })
    } else if (readingFilter.label === 'Custom') {
      analytics.postAnalyticsEvent({
        category: `reading_filter_dashboardDigitalScale_customReadings`,
        action: `reading_filter_dashboardDigitalScale_customReadings`,
        label: `Button`,
        screenName: 'DigitalScaleDashboard',
      })
    }
  }, [readingFilter])

  useEffect(() => {
    if (dateRange.startDate && dateRange.endDate) {
      setChartReadings(
        mapToChartReadings(
          scaleReadings.filter((reading) => {
            const date1 = new Date(dayjs(reading.readingDate).format('YYYY-MM-DD')).getTime()
            if (
              date1 >= new Date(dayjs(dateRange.startDate).format('YYYY-MM-DD')).getTime() &&
              date1 <= new Date(dayjs(dateRange.endDate).format('YYYY-MM-DD')).getTime()
            ) {
              return reading
            }
          }),
        ),
      )
      const filtered = scaleReadings.filter((reading) => {
        const date1 = new Date(dayjs(reading.readingDate).format('YYYY-MM-DD')).getTime()
        if (
          date1 >= new Date(dayjs(dateRange.startDate).format('YYYY-MM-DD')).getTime() &&
          date1 <= new Date(dayjs(dateRange.endDate).format('YYYY-MM-DD')).getTime()
        ) {
          return reading
        }
      })
      setFatReadings(
        filtered
          .filter((r) => r.bodyFatPercent && r.bodyFatPercent > 0)
          .map((reading, i) => ({
            value: reading.bodyFatPercent!,
            date: `${dayjs(reading.readingDate).format('M/DD/YY')}\n${dayjs(reading.readingDate).format('h:mm:ss a')}`,
            type: 'high',
            xIndex: i,
          })),
      )
      setMuscleReadings(
        filtered
          .filter((r) => r.musclePercent && r.musclePercent > 0)
          .map((reading, i) => ({
            value: reading.musclePercent!,
            date: `${dayjs(reading.readingDate).format('M/DD/YY')}\n${dayjs(reading.readingDate).format('h:mm:ss a')}`,
            type: 'high',
            xIndex: i,
          })),
      )
      setWaterReadings(
        filtered
          .filter((r) => r.waterPercent && r.waterPercent > 0)
          .map((reading, i) => ({
            value: reading.waterPercent!,
            date: `${dayjs(reading.readingDate).format('M/DD/YY')}\n${dayjs(reading.readingDate).format('h:mm:ss a')}`,
            type: 'high',
            xIndex: i,
          })),
      )

      setReadings(
        filtered.filter((reading) => {
          const date1 = new Date(dayjs(reading.readingDate).format('YYYY-MM-DD')).getTime()
          if (
            date1 >= new Date(dayjs(dateRange.startDate).format('YYYY-MM-DD')).getTime() &&
            date1 <= new Date(dayjs(dateRange.endDate).format('YYYY-MM-DD')).getTime()
          ) {
            return reading
          }
        }),
      )
    }
  }, [dateRange])

  const mapToChartReadings = (readings: ScaleReading[]) => {
    if (readings && readings.length > 0) {
      return readings.map((reading) => ({
        value: reading.value,
        date: `${dayjs(reading.readingDate).format('M/DD/YY')}\n${dayjs(reading.readingDate).format('h:mm:ss a')}`,
        type: 'high',
      }))
    }
  }

  const setMobileSetting = () => {
    if (window.innerWidth < 800) {
      setChartHeight(300)
      setIsMobile(true)
    } else {
      setChartHeight(300)
      setIsMobile(false)
    }
  }

  function changeReadingFilter(option) {
    setReadingFilter(option)
    if (option.value && option.value !== 0 && scaleReadings && scaleReadings.length > 0) {
      setShowCustomFields(false)
      setChartReadings(
        mapToChartReadings(
          scaleReadings.filter((reading) => {
            const date1 = new Date(dayjs(reading.readingDate).format('YYYY-MM-DD')).getTime()
            const date2 = new Date(dayjs().subtract(option.value, 'day').format('YYYY-MM-DD')).getTime()
            if (date1 >= date2) {
              return reading
            }
          }),
        ),
      )
      const filtered = scaleReadings.filter((reading) => {
        const date1 = new Date(dayjs(reading.readingDate).format('YYYY-MM-DD')).getTime()
        const date2 = new Date(dayjs().subtract(option.value, 'day').format('YYYY-MM-DD')).getTime()
        if (date1 >= date2) {
          return reading
        }
      })
      setFatReadings(
        filtered
          .filter((r) => r.bodyFatPercent && r.bodyFatPercent > 0)
          .map((reading, i) => ({
            value: reading.bodyFatPercent ?? 0,
            date: `${dayjs(reading.readingDate).format('M/DD/YY')}\n${dayjs(reading.readingDate).format('h:mm:ss a')}`,
            type: 'high',
            xIndex: i,
          })),
      )
      setMuscleReadings(
        filtered
          .filter((r) => r.musclePercent && r.musclePercent > 0)
          .map((reading, i) => ({
            value: reading.musclePercent ?? 0,
            date: `${dayjs(reading.readingDate).format('M/DD/YY')}\n${dayjs(reading.readingDate).format('h:mm:ss a')}`,
            type: 'high',
            xIndex: i,
          })),
      )
      setWaterReadings(
        filtered
          .filter((r) => r.waterPercent && r.waterPercent > 0)
          .map((reading, i) => ({
            value: reading.waterPercent ?? 0,
            date: `${dayjs(reading.readingDate).format('M/DD/YY')}\n${dayjs(reading.readingDate).format('h:mm:ss a')}`,
            type: 'high',
            xIndex: i,
          })),
      )
      setReadings(
        scaleReadings.filter((reading) => {
          const date1 = new Date(dayjs(reading.readingDate).format('YYYY-MM-DD')).getTime()
          const date2 = new Date(dayjs().subtract(option.value, 'day').format('YYYY-MM-DD')).getTime()
          if (date1 >= date2) {
            return reading
          }
        }),
      )
      setDateRange({
        startDate: undefined,
        endDate: undefined,
      })
    } else if (option.value === 0) {
      setShowCustomFields(true)
    } else {
      setShowCustomFields(false)
      setChartReadings(mapToChartReadings(scaleReadings))
      setReadings(scaleReadings)
      setFatReadings(
        scaleReadings
          .filter((r) => r.bodyFatPercent && r.bodyFatPercent > 0)
          .map((reading, i) => ({
            value: reading.bodyFatPercent ?? 0,
            date: `${dayjs(reading.readingDate).format('M/DD/YY')}\n${dayjs(reading.readingDate).format('h:mm:ss a')}`,
            type: 'high',
            xIndex: i,
          })),
      )
      setMuscleReadings(
        scaleReadings
          .filter((r) => r.musclePercent && r.musclePercent > 0)
          .map((reading, i) => ({
            value: reading.musclePercent ?? 0,
            date: `${dayjs(reading.readingDate).format('M/DD/YY')}\n${dayjs(reading.readingDate).format('h:mm:ss a')}`,
            type: 'high',
            xIndex: i,
          })),
      )
      setWaterReadings(
        scaleReadings
          .filter((r) => r.waterPercent && r.waterPercent > 0)
          .map((reading, i) => ({
            value: reading.waterPercent ?? 0,
            date: `${dayjs(reading.readingDate).format('M/DD/YY')}\n${dayjs(reading.readingDate).format('h:mm:ss a')}`,
            type: 'high',
            xIndex: i,
          })),
      )
      setDateRange({
        startDate: undefined,
        endDate: undefined,
      })
    }
  }

  useEffect(() => {
    if (welltrak === 0 && profile) {
      setWelltrak(profile.welltrakPID)
    }
  }, [])

  const onSubmitForm = (value) => {
    dispatch(setLoading(true))
    updateWeight(value.wDate, welltrak, value.wTime, value.wWeight)
      .then((response) => {
        if (!response.data) {
          navigate('/error')
        }
        dispatch(
          loadScaleReadingSummary({
            token: accessToken,
          }),
        )
        setAddWeightModal(false)
      })
      .finally(() => {
        dispatch(setLoading(false))
      })
  }

  return (
    <>
      <div className="dashboard-page-container">
        {isMobile && <div style={{ minHeight: 20 }} />}

        <ResponsiveContainer
          className="dashboard-filter-row"
          styles={{
            width: isMobile ? '100%' : 'auto',
            height: 'auto',
            display: 'flex',
          }}
        >
          <div className="scale-chart-container">
            <Row>
              <Dropdown className="date-filter-select-container">
                <Dropdown.Toggle variant="secondary" id="dropdown-basic" className="date-filter-select">
                  {readingFilter.label}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {filterOptions.map((option) => (
                    <Dropdown.Item onClick={() => changeReadingFilter(option)}>{option.label}</Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
              {showCustomFields && (
                <>
                  <div
                    style={{
                      marginLeft: isMobile ? 0 : 5,
                      minWidth: isMobile ? '100%' : 'none',
                      marginBottom: isMobile ? 10 : 0,
                      marginTop: isMobile ? 10 : 0,
                    }}
                  >
                    <ReactDatePicker
                      selected={dateRange.startDate}
                      className="start-date-picker"
                      onChange={(date: Date) => {
                        setDateRange((prevState) => ({
                          ...prevState,
                          startDate: date,
                        }))
                      }}
                      showYearDropdown
                      scrollableYearDropdown
                      placeholderText="Start Date"
                      maxDate={new Date()}
                      isClearable={false}
                    />
                  </div>
                  <div style={{ minWidth: 10 }} />
                  <div style={{ minWidth: isMobile ? '100%' : 'none', marginBottom: isMobile ? 5 : 0 }}>
                    <ReactDatePicker
                      selected={dateRange.endDate}
                      className="start-date-picker"
                      onChange={(date: Date) => {
                        setDateRange((prevState) => ({
                          ...prevState,
                          endDate: date,
                        }))
                      }}
                      showYearDropdown
                      scrollableYearDropdown
                      placeholderText="End Date"
                      maxDate={new Date()}
                    />
                  </div>
                  <div style={{ minWidth: 10 }} />
                </>
              )}

              <Button
                text="Update My Weight"
                backgroundColor={theme.colors.primary1}
                rightIcon="plus"
                onClick={() => setAddWeightModal(!addWeightModal)}
                styles={
                  isMobile
                    ? { marginTop: 20 }
                    : {
                        marginLeft: 'auto',
                        width: isMobile ? '100%' : 'auto',
                      }
                }
              />
            </Row>
          </div>
        </ResponsiveContainer>

        <div className="scale-chart-container">
          <LineChart
            isMobile={isMobile}
            chartReadings={chartReadings}
            chartHeight={chartHeight}
            hasBodyComposition={scaleReadingsSummary.hasBodyComposition}
            fatReadings={fatReadings}
            muscleReadings={muscleReadings}
            waterReadings={waterReadings}
          />
        </div>
        {chartReadings && chartReadings.length > 0 && (
          <GridCards
            scaleReadings={scaleReadings}
            scaleReadingsSummary={scaleReadingsSummary}
            hasBodyComposition={scaleReadingsSummary.hasBodyComposition}
          />
        )}
        {readings && readings.length > 0 && (
          <ReadingDataGrid readings={readings} isMobile={isMobile} hasBodyComposition={scaleReadingsSummary.hasBodyComposition} />
        )}

        <div className="dashboard-resources-section">
          <ContentCards type="Digital Scale" />
        </div>
      </div>

      <AddWeightModal modalOpen={addWeightModal} toggleModal={(bool) => setAddWeightModal(bool)} onSubmit={onSubmitForm} />
    </>
  )
}
